
import { annotationSourceReference, canAnnotateImage } from '@/utils/annotationSource';
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import RunnitDialog from '@/views/Runnits/base/RunnitDialog.vue';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import SelectedColumnsButton from '@/components/ImageGallery/SelectedColumnsButton.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import InfoMessageSmall from '@/components/base/InfoMessageSmall.vue';
import _cloneDeep from 'lodash/cloneDeep';
import { isImgAnnotated, replaceTargetFromDialogItem } from '@/utils/imgsVisualPromptTarget';
import { reconcileSelectedImages } from '@/components/ImageGallery/selectedImagesReconciliation';

export default Vue.extend({
	name: 'SelectedImagesDialog',
	mixins: [RunnitsImageSelectMixin, RunnitBulkActionsMixin],
	data () {
		return {
			initialSelectionSnapshot: null as any,
			isConfirmed: false,
			localSelectedImages: [] as Array<{ type: string; data: any; key: string }>,
			localSelectionLimit: null as number | null,
			localAwaitingField: null as any,
			localSelectedColumns: 4 as number,
			resetConfirmOpen: false,
			pendingResetItem: null as { type: string; data: any } | null,
		};
	},
	computed: {
		...mapState(['runnitState']),
		selectedImagesDialogOpen: {
			get (): boolean {
				return this.runnitState.selectedImagesDialogOpen;
			},
			set (val: boolean) {
				if (!val) {
					this.onConfirm();
				} else {
					this.updateRunnitState({ selectedImagesDialogOpen: val });
				}
			},
		},
		currentSelectionCount (): number {
			// Drive the dialog UI off the local list so it doesn't
			// flicker or clear when the underlying form array updates.
			return this.localSelectedImages.length;
		},
		selectionLimit (): number | null {
			// Keep the limit stable while the global selection state changes.
			return this.localSelectionLimit;
		},
		isSelectionLimitExceeded (): boolean {
			if (!this.selectionLimit) return false;
			return this.currentSelectionCount > this.selectionLimit;
		},
		allSelectedImages (): Array<{ type: string; data: any; key: string }> {
			return this.localSelectedImages;
		},
		/**
		 * The field this dialog was opened for.
		 *
		 * Prefer the copy captured on open. `currentSelectionState` is a global getter
		 * returning whichever active state has the highest sortOrder, and
		 * getOrderedSelectionStates drops any state whose sortOrder is falsy. Capturing
		 * the field keeps annotation actions available while selection updates settle.
		 */
		awaitingField (): any {
			return this.localAwaitingField ||
				this.currentSelectionState?.fieldAwaitingImageGallerySelection ||
				null;
		},
		hasVisualPrompt (): boolean {
			return !!this.awaitingField?.display?.hasVisualPrompt;
		},
		/** Annotate originals for the current selection, keyed by image identity. */
		originalImagesMap (): Record<string, any> {
			return this.currentSelectionState?.originalImages || {};
		},
		/** Cheap fingerprint so we resync when Vuex selection order/ids change without deep-watching all of selection state. */
		selectionStateOrderedIdsFingerprint (): string {
			if (!this.currentSelectionState) return '';
			const su = this.currentSelectionState.selectedUploads || [];
			const sr = this.currentSelectionState.selectedNodeRunResults || [];
			return JSON.stringify([su.map((u: { id?: string }) => u?.id), sr.map((r: { uuid?: string }) => r?.uuid)]);
		},
	},
	watch: {
		selectionStateOrderedIdsFingerprint () {
			if (!this.runnitState.selectedImagesDialogOpen) return;
			if (!this.currentSelectionState) return;
			const reconciled = reconcileSelectedImages(
				this.localSelectedImages,
				this.buildImagesFromSelection(this.currentSelectionState),
			);
			if (reconciled !== this.localSelectedImages) {
				this.localSelectedImages = reconciled;
			}
		},
		'runnitState.selectedImagesDialogOpen': {
			handler (val) {
				if (val) {
					this.initialSelectionSnapshot = null;
					if (this.currentSelectionState && this.currentSelectionState.sortOrder > 0) {
						this.initialSelectionSnapshot = _cloneDeep(this.currentSelectionState);
					}
					// Hold the field and its limit locally while the dialog is open.
					this.localAwaitingField = this.currentSelectionState?.fieldAwaitingImageGallerySelection || null;
					this.localSelectionLimit = this.localAwaitingField?.display?.maxImages || null;
					this.localSelectedImages = this.buildImagesFromSelection(this.currentSelectionState);
				} else {
					this.initialSelectionSnapshot = null;
					this.localSelectedImages = [];
					this.localSelectionLimit = null;
					this.localAwaitingField = null;
				}
			},
		},
		// Watch for completed visual prompt replacements
		'runnitState.visualPromptEditorStates': {
			deep: true,
			handler (newStates: any) {
				if (!this.runnitState.selectedImagesDialogOpen || !newStates) return;

				// Field captured on open — see awaitingField for why not currentSelectionState
				const fieldUuid = this.awaitingField?.uuid;
				if (!fieldUuid) return;

				const editorState = newStates[fieldUuid];
				if (editorState?.completedReplacement) {
					const { upload } = editorState.completedReplacement;
					const localIndex = this.localSelectedImages.findIndex(
						(li) => li.type === 'upload' && li.data?.id === upload.id,
					);

					if (localIndex >= 0 && localIndex < this.localSelectedImages.length && upload) {
						const newLocalImages = [...this.localSelectedImages];
						newLocalImages[localIndex] = {
							type: 'upload',
							data: upload,
							key: `upload-${upload.id}`,
						};
						this.localSelectedImages = newLocalImages;
					}

					// Clear the completedReplacement flag
					this.updateVisualPromptEditorState({
						fieldUuid,
						editorState: {
							completedReplacement: null,
						},
					});
				}
			},
		},
	},
	methods: {
		canAnnotateImage,
		...mapActions(['updateRunnitState', 'updateVisualPromptEditorState']),
		buildImagesFromSelection (selectionState: any): Array<{ type: string; data: any; key: string }> {
			if (!selectionState) return [];
			const uploads = (selectionState.selectedUploads || []).map((upload: any) => ({
				type: 'upload',
				data: upload,
				key: `upload-${upload.id}`,
			}));
			const results = (selectionState.selectedNodeRunResults || []).map((result: any) => ({
				type: 'result',
				data: result,
				key: `result-${result.uuid}`,
			}));
			return [...uploads, ...results];
		},
		onCancel () {
			// If closing, and NOT confirmed -> Revert
			if (!this.isConfirmed) {
				this.revertSelection();
			}

			if (this.currentSelectionState && this.currentSelectionState.triggeringId) {
				this.exitSelectionMode(this.currentSelectionState.triggeringId);
			}

			this.isConfirmed = false; // Reset flag
			if (this.runnitState.selectedImagesDialogOpen) {
				this.updateRunnitState({ selectedImagesDialogOpen: false });
			}
		},
		revertSelection () {
			if (this.initialSelectionSnapshot) {
				this.updateSelectionState(this.initialSelectionSnapshot);
				// Also revert local images to the snapshot
				this.localSelectedImages = this.buildImagesFromSelection(this.initialSelectionSnapshot);
			}
		},
		onRevert () {
			this.revertSelection();
			this.onCancel();
		},
		onConfirm () {
			this.isConfirmed = true;
			this.onCancel();
		},
		onItemClick (item: { type: string; data: any }) {
			if (item.type === 'upload') {
				this.onUploadMultiSelection(item.data);
				// Optimistically update local list so deselected image disappears
				this.localSelectedImages = this.localSelectedImages.filter(
					(i) => !(i.type === 'upload' && i.data.id === item.data.id),
				);
			} else {
				this.onNodeRunResultMultiSelection(
					item.data.nodeRunId ? { id: item.data.nodeRunId } : null,
					item.data
				);
				this.localSelectedImages = this.localSelectedImages.filter(
					(i) => !(i.type === 'result' && i.data.uuid === item.data.uuid),
				);
			}
		},
		async onVisualPromptClick (item: { type: string; data: any }) {
			if (!this.hasVisualPrompt || !canAnnotateImage(item.data)) {
				return;
			}
			const baseSource = annotationSourceReference(item.data, item.type === 'upload' ? 'upload' : 'nodeRunResult');
			if (!baseSource) return;

			const fieldUuid = this.awaitingField.uuid;
			if (!fieldUuid) {
				return;
			}

			const replaceImageTarget = replaceTargetFromDialogItem(item);
			if (!replaceImageTarget) {
				console.error('SelectedImagesDialog: missing replace target for annotate');
				return;
			}

			// Fetch the image URL
			let imageUrl: string | null = null;
			try {
				if (item.type === 'upload') {
					const { imageUrl: url } = await this.fetchSrc(item.data, 'upload');
					imageUrl = url;
				} else {
					const { imageUrl: url } = await this.fetchSrc(item.data, 'nodeRunResult');
					imageUrl = url;
				}
			} catch (err) {
				console.error('Error fetching image URL for visual prompt editor:', err);
				return;
			}

			if (!imageUrl) {
				return;
			}

			// Close other menus globally
			this.updateRunnitState({
				promptGeneratorOpen: false,
				imageSelectOpen: false,
			});

			// Open the Visual Prompt Editor with replacement context
			this.updateVisualPromptEditorState({
				fieldUuid,
				editorState: {
					open: true,
					backgroundImageUrl: imageUrl,
					baseSource,
					replaceImageTarget,
				},
			});
		},
		/** Whether this tile is an annotation composite (has a distinct original to revert to). */
		isAnnotated (item: { type: string; data: any }): boolean {
			return isImgAnnotated(this.originalImagesMap, item);
		},
		/**
		 * Ask before reverting: the reset commits immediately to the field value, so the
		 * dialog's Cancel won't undo it — the annotations are gone for good once confirmed.
		 */
		onResetClick (item: { type: string; data: any }) {
			this.pendingResetItem = item;
			this.resetConfirmOpen = true;
		},
		/**
		 * Revert the confirmed image to its original without opening the editor. Routed to the
		 * field owner via field.uuid (same channel as annotate), because the field value —
		 * the source of truth for the reset — lives in the owning component, not the dialog.
		 * The owner performs the reset and re-syncs selection state; our reconcile watcher
		 * then swaps the tile.
		 */
		onResetConfirmed (closeConfirm: () => void) {
			const item = this.pendingResetItem;
			this.pendingResetItem = null;
			if (typeof closeConfirm === 'function') closeConfirm();
			else this.resetConfirmOpen = false;
			if (!item) return;

			const fieldUuid = this.awaitingField?.uuid;
			if (!fieldUuid) return;
			const target = replaceTargetFromDialogItem(item);
			if (!target) return;
			this.updateVisualPromptEditorState({
				fieldUuid,
				editorState: { resetToOriginalRequest: target },
			});
		},
	},
	components: {
		RunnitDialog,
		RunnitImage,
		SelectedColumnsButton,
		BaseButton,
		ConfirmDialog,
		InfoMessageSmall,
	},
});
